import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  Input,
  OnInit,
  type OnChanges,
  type SimpleChanges,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { PaymentCheckoutWizardModule } from '@app/account/shared/payment-checkout-wizard/payment-checkout-wizard.module';
import { AccountsBffService } from '@app/core/services/bff/accounts-bff.service';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';

import { UserServiceCardFullModule } from '@app/meetings/user-service-card-full/user-service-card-full.module';
import { UserServiceSurfaceBlockComponent } from '@app/meetings/user-service-card-full/user-service-surface-block/user-service-surface-block.component';
import { IStatsList } from '@app/teachers/teacher-card/teacher-card-variables';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import {
  AccountService,
  AuthService,
  BillingV2Service,
  ContextService,
  FeaturesService,
  FilesService,
  mapAccountToUser,
} from '@core/services';
import { HelpersBffService } from '@core/services/bff/helpers.service';
import { UserProfileBffService, UserProfileInfo } from '@core/services/bff/user-profile.service';
import { ChangableComponent } from '@models/changable.component';

import { ApplicationDialogsService } from '@shared/application-dialogs';
import { IconComponent } from '@shared/icon/icon.component';
import { RatingComponent } from '@shared/rating/rating.component';
import { IAccountStats, TenantEnum, User, UserService, UserStatusEnum } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-teacher-booking-surface-block',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    IconComponent,
    MobileModalModule,
    ModalModule,
    OnUiButtonModule,
    OnUiCoverModule,
    PaymentCheckoutWizardModule,
    RouterModule,
    UserServiceCardFullModule,
    RatingComponent,
    UserServiceSurfaceBlockComponent,
  ],
  templateUrl: './teacher-booking-surface-block.component.html',
  styleUrls: ['./teacher-booking-surface-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeacherBookingSurfaceBlockComponent extends ChangableComponent implements OnInit, OnChanges {
  deviceService = inject(DeviceDetectorService);
  features = inject(FeaturesService);
  billingV2Service = inject(BillingV2Service);
  contextService = inject(ContextService);
  authService = inject(AuthService);
  accountService = inject(AccountService);
  accountsBffService = inject(AccountsBffService);
  location = inject(Location);
  dialogsService = inject(ApplicationDialogsService);
  helpers = inject(HelpersBffService);
  userProfileBff = inject(UserProfileBffService);

  teacher = input<User>();

  public me: User;
  public isLoaded: boolean;
  public isGuest: boolean;
  public isMe: boolean;
  public checkoutModalOpen: boolean;
  public userServiceCardFullInModal: boolean;
  public profileUrl: string[];
  public fullName: string;
  public stats: Partial<IAccountStats>;
  public avatar: OnUiCover;
  public openedUserService: UserService;
  private originalUrl: string;
  public surfaceBlockDisplay: boolean;
  public profileVerified: boolean = true;
  public profile: UserProfileInfo;
  public statsLine1: Array<Partial<IStatsList>> = [];
  public statsLine2: Array<Partial<IStatsList>> = [];

  public ngOnInit() {
    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this.me = me;
        this.isGuest = me && me.status === UserStatusEnum.guest;
        this.isMe = me?.id === this.teacher()?.id;
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    this.userProfileBff
      .getUserProfileInfo({ id: this.teacher().id })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (profile) => {
          this.profile = profile;
          this.prepareUserStats();
          this.isLoaded = true;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    // если в visit_info не определена can_sign_up, то запрашиваем
    // данная ситуация возможна, потому что объект пользователя может прийти из объекта сущности и
    // не будет содержать visit_info.can_sign_up
    if (typeof this.teacher()?.visit_info?.can_sign_up !== 'boolean') {
      this.accountsBffService
        .getOne({ id: this.teacher()?.id, details: ['visit_info', 'teacher_profile_verified'] })
        .pipe(map((account) => mapAccountToUser(account)))
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (user) => {
            this.teacher().visit_info = user.visit_info;
            this.teacher().teacher_profile_verified = this.profileVerified = user.teacher_profile_verified;
            this.detectChanges();
          },
        });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.teacher && changes.teacher.currentValue) {
      this.prepareUserStats();
      this.prepareTeacher();
      this.detectChanges();
    }
  }

  private prepareTeacher() {
    this.profileUrl = AccountService.accountRoute(this.teacher());
    this.fullName = AccountService.getUserFullName(this.teacher());
    this.stats = this.teacher()?.stats;
    this.avatar = {
      type: 'avatar',
      img:
        this.prepareStorageUrl(this.teacher().userpic?.md?.url) ||
        FilesService.getFileUrlBySize(this.teacher().userpic_id),
      link: this.profileUrl.join('/'),
      title: this.fullName,
      form: 'circle',
    };
  }

  public get minPriceTier(): number {
    return this.stats?.min_price_tier;
  }

  private prepareUserStats() {
    this.statsLine1 = [
      {
        name: 'lessons',
        title: 'user-profile.stats.lessons',
        value: this.profile?.numberOfLessons || 0,
      },
      {
        name: 'subscribers',
        title: 'user-profile.stats.subscribers',
        value: this.profile?.numberOfSubscribers || 0,
      },
      {
        name: 'subscriptions',
        title: 'user-profile.stats.subscriptions',
        value: this.profile?.numberOfSubscriptions || 0,
      },
    ];

    this.statsLine2 = [
      {
        name: 'lessons',
        title: 'user-profile.stats.classes',
        value: this.profile?.numberOfMeetings || 0,
      },
      {
        name: 'students',
        title: 'user-profile.stats.students',
        value: this.profile?.numberOfStudents || 0,
      },
    ];
  }

  public getFormattedAmount(): Observable<string> {
    return this.billingV2Service.formatAmount$(this.minPriceTier);
  }

  public onToggleUserServiceInModal(service?: UserService) {
    this.userServiceCardFullInModal = !this.userServiceCardFullInModal;
    this.openedUserService = this.userServiceCardFullInModal ? service : null;
    this.setAndResetUrlForUserService();
    this.detectChanges();
  }

  public openCheckoutModal() {
    const onAuthComplete = () => {
      this.checkoutModalOpen = true;
      this.dialogsService.openUserServiceCheckoutWizard({
        teacherId: this.teacher()?.id,
      });
      this.detectChanges();
    };

    if (this.isGuest) {
      this.authService.showAuthModal(onAuthComplete);
    } else {
      onAuthComplete();
    }
  }

  public closeCheckoutModal() {
    this.checkoutModalOpen = false;
    this.detectChanges();
  }

  private setAndResetUrlForUserService() {
    const url = this.helpers.userServiceRouteUniversal(this.openedUserService);
    const currentUrl = this.location.path(false);
    if (this.userServiceCardFullInModal) {
      if (currentUrl !== url) {
        if (!this.originalUrl) {
          this.originalUrl = currentUrl;
        }
        this.location.replaceState(url);
      }
    } else {
      if (this.originalUrl) {
        this.location.replaceState(this.originalUrl);
        this.originalUrl = null;
      }
    }
  }

  public onIinViewportChange(e: boolean) {
    this.surfaceBlockDisplay = e;
    this.detectChanges();
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  public trackByFn(index) {
    return index;
  }
}
